import { Rgb } from "./tracks";

// The contract between renderer.ts, which decides what a frame shows, and the
// backends that draw it: WebGPU (backend-gpu.ts) or WebGL (backend-gl.ts).

/** A vertex in MeshBuilder's layout: position, normal, colour, uv. */
export const FLOATS_PER_VERTEX = 11;
/** A Blender model's vertex: float32×3 position, snorm8×4 normal, unorm8×4 colour + paint slot. */
export const PACKED_STRIDE = 20;

export type Blend = "opaque" | "alpha" | "add";

export interface Mesh {
  count: number;
  packed: boolean;
  /** Where to start in the index buffer, for a part of a model — a face, a pose. */
  first?: number;
}

/** Rebuilt every frame: `quads` of `data` are live, drawn as two triangles each. */
export interface DynamicMesh extends Mesh {
  data: Float32Array;
  quads: number;
  maxQuads: number;
}

/** What a model's paint slots are filled with. */
export interface Paint {
  body: Float32Array;
  accent: Float32Array;
  skin: Float32Array;
  hair: Float32Array;
}

export interface Draw {
  mesh: Mesh;
  /** 0 lit, 1 road, 2 boost pad, 3 sprite, 4 soft blob (shadow, smoke), 5 flame, 6 glow, 7 beam, 8 unlit (the sky), 9 lit with a grain (ground, road), 10 lit and dither-faded to the tint's alpha — see the shaders. */
  mode: number;
  blend: Blend;
  model: Float32Array;
  tint: Float32Array;
  /** Painted, with the plastic sheen of a model — or null, for the world. */
  paint: Paint | null;
}

/**
 * A frame to draw. Backends draw it in three passes — opaque, then see-through
 * (`alpha`), then fire (`add`) — so commands can be pushed in any order.
 */
export interface Frame {
  viewProj: Float32Array;
  eye: Float32Array;
  fog: Rgb;
  time: number;
  /** 1 for the revamp's light (REVAMP_LOG.md), 0 for the old. */
  look: number;
  /** The revamp's light: the way to the sun, the hemisphere's sky and its ground — a vec4 apiece, the fourth unused. */
  light: Float32Array;
  draws: Draw[];
  count: number;
}

export interface Backend {
  kind: "webgpu" | "webgl";
  /** WebGPU's clip space runs 0…1 in depth, GL's -1…1. */
  depthZeroToOne: boolean;
  /** Set once the GPU has gone away (WebGPU can lose its device); the renderer starts over. */
  lost: boolean;
  mesh(vertices: Float32Array | Uint8Array, indices: Uint16Array, packed: boolean): Mesh;
  dynamicMesh(maxQuads: number): DynamicMesh;
  /** Push a dynamic mesh's live quads to the GPU. */
  updateDynamic(mesh: DynamicMesh): void;
  free(mesh: Mesh): void;
  texture(source: HTMLCanvasElement): void;
  resize(width: number, height: number): void;
  render(frame: Frame): void;
  destroy(): void;
}

/** Most a frame will ever draw; anything past it is dropped, not crashed on. */
export const MAX_DRAWS = 1024;

/** The indices for `quads` quads laid out four vertices apiece, as the dynamic meshes are. */
export function quadIndices(quads: number) {
  const out = new Uint16Array(quads * 6);
  for (let q = 0; q < quads; q++) out.set([q * 4, q * 4 + 1, q * 4 + 2, q * 4, q * 4 + 2, q * 4 + 3], q * 6);
  return out;
}
